'use client';
import React from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

export default function EcommerceCaseStudy() {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-10" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(43,98,239,0.06), transparent, rgb(8,11,18))' }} />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-8">
          <Link href="/case-studies" className="inline-flex items-center gap-2 text-cobalt text-sm font-medium mb-8 hover:text-cobalt-glow transition-colors">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m15 18-6-6 6-6" />
            </svg>
            Back to Case Studies
          </Link>
          <span className="text-cobalt font-mono text-sm tracking-widest uppercase mb-4 block">E-Commerce</span>
          <h1 className="font-display font-bold text-4xl lg:text-5xl text-silver-text leading-tight mb-6 max-w-4xl">
            Multilingual Localization for a Leading Global eCommerce Platform
          </h1>
          <p className="text-lg text-silver-muted max-w-2xl leading-relaxed mb-10">
            20+ language eCommerce localization with 99% first-delivery acceptance, 99% on-time delivery, and custom workflow tooling - delivering culturally nuanced app interfaces worldwide.
          </p>
          <div className="flex flex-wrap gap-6">
            {[
              { value: '99%', label: 'First Delivery Acceptance' },
              { value: '99%', label: 'On-Time Delivery' },
              { value: '20+', label: 'Languages' },
              { value: '40%', label: 'Fewer Revision Cycles' },
            ]?.map((stat) => (
              <div key={stat?.label} className="glow-card rounded-xl px-6 py-4 text-center min-w-[120px]">
                <div className="text-xl font-bold text-cobalt-glow">{stat?.value}</div>
                <div className="text-xs text-silver-muted mt-1">{stat?.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Content */}
      <section className="py-16 lg:py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2 space-y-12">

              {/* Challenge */}
              <div>
                <h2 className="font-display font-bold text-2xl text-silver-text mb-5 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-cobalt/20 border border-cobalt/30 flex items-center justify-center text-cobalt-glow text-sm font-mono">01</span>
                  The Challenge
                </h2>
                <p className="text-silver-muted leading-relaxed mb-4">
                  A leading global eCommerce platform needed high-volume localization of their app interface across 20+ languages with tight deadlines, budget control, culturally accurate output, and repeatable quality standards for each market.
                </p>
                <p className="text-silver-muted leading-relaxed">
                  The challenge was delivering 15,000+ words per day per language while maintaining cultural nuance, managing complex text extraction from app files, and building scalable quality assurance processes across a wide language set.
                </p>
              </div>

              {/* Scope */}
              <div>
                <h2 className="font-display font-bold text-2xl text-silver-text mb-5 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-cobalt/20 border border-cobalt/30 flex items-center justify-center text-cobalt-glow text-sm font-mono">02</span>
                  Project Scope & Deliverables
                </h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {[
                    { title: 'Translated App Files', desc: 'Localized interface strings across all 20+ languages' },
                    { title: 'Style Guides', desc: 'New style guides created for 10+ languages ensuring cultural accuracy' },
                    { title: 'QA Guidelines', desc: '20+ language-specific QA guidelines for ongoing quality control' },
                    { title: 'Glossaries', desc: 'Terminology glossaries for consistent eCommerce vocabulary across markets' },
                    { title: 'Custom Extraction Tool', desc: 'Into23-developed text extraction and restoration tool for app file processing' },
                    { title: 'Simplified Chinese Style Guide', desc: '10-page guide covering punctuation, terminology, grammar, and cultural considerations' },
                  ]?.map((item) => (
                    <div key={item?.title} className="glow-card rounded-xl p-5">
                      <h3 className="font-semibold text-silver-text text-sm mb-1">{item?.title}</h3>
                      <p className="text-xs text-silver-muted">{item?.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Production Process */}
              <div>
                <h2 className="font-display font-bold text-2xl text-silver-text mb-5 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-cobalt/20 border border-cobalt/30 flex items-center justify-center text-cobalt-glow text-sm font-mono">03</span>
                  Production Process
                </h2>
                <div className="space-y-4">
                  {[
                    { step: '1', title: 'Text Preparation', desc: 'Structured intake and preparation of source app files for localization.' },
                    { step: '2', title: 'Text Extraction & Word Count Analysis', desc: 'Custom Into23-developed tools extract translatable strings and analyze word counts for accurate scoping.' },
                    { step: '3', title: 'Translation & Localization', desc: 'Professional linguists with eCommerce expertise translate and localize content using professional CAT tools.' },
                    { step: '4', title: 'Quality Assurance', desc: 'Multi-stage QA using professional CAT tools, language-specific QA guidelines, and native reviewer checks.' },
                    { step: '5', title: 'Text Restoration & Delivery', desc: 'Custom tools restore translated text into original app file formats, ready for integration.' },
                    { step: '6', title: 'Glossary Maintenance', desc: 'Ongoing terminology management to ensure consistency across all future localization batches.' },
                  ]?.map((item) => (
                    <div key={item?.step} className="flex gap-4 p-5 glow-card rounded-xl">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-cobalt/20 border border-cobalt/30 flex items-center justify-center text-cobalt-glow text-sm font-bold">{item?.step}</div>
                      <div>
                        <h3 className="font-semibold text-silver-text text-sm mb-1">{item?.title}</h3>
                        <p className="text-xs text-silver-muted leading-relaxed">{item?.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Languages */}
              <div>
                <h2 className="font-display font-bold text-2xl text-silver-text mb-5 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-cobalt/20 border border-cobalt/30 flex items-center justify-center text-cobalt-glow text-sm font-mono">04</span>
                  Language Coverage
                </h2>
                <div className="flex flex-wrap gap-2">
                  {['Chinese (Simplified)', 'Chinese (Traditional)', 'Japanese', 'Korean', 'Thai', 'Vietnamese', 'Indonesian', 'Malay', 'Hindi', 'Bengali', 'French', 'German', 'Spanish', 'Italian', 'Portuguese (Brazil)', 'Dutch', 'Russian', 'Arabic', 'Turkish', 'Polish', 'English (US)', 'English (UK)']?.map((lang) => (
                    <span key={lang} className="px-3 py-1.5 rounded-lg bg-cobalt/10 border border-cobalt/20 text-cobalt-glow text-xs font-mono">{lang}</span>
                  ))}
                </div>
              </div>

              {/* Results */}
              <div>
                <h2 className="font-display font-bold text-2xl text-silver-text mb-5 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-cobalt/20 border border-cobalt/30 flex items-center justify-center text-cobalt-glow text-sm font-mono">05</span>
                  Results
                </h2>
                <div className="grid sm:grid-cols-2 gap-4 mb-6">
                  {[
                    { metric: '99% first delivery acceptance', detail: 'Across all 20+ language pairs' },
                    { metric: '20+ style guides & QA guidelines', detail: 'Enabling consistent quality at scale' },
                    { metric: '99% on-time delivery', detail: 'Meeting all client deadlines' },
                    { metric: '40% fewer revision cycles', detail: 'Through the Simplified Chinese style guide alone' },
                  ]?.map((r) => (
                    <div key={r?.metric} className="glow-card rounded-xl p-5 border-l-2 border-cobalt/40">
                      <div className="font-semibold text-cobalt-glow text-sm mb-1">{r?.metric}</div>
                      <div className="text-xs text-silver-muted">{r?.detail}</div>
                    </div>
                  ))}
                </div>
                <div className="glow-card rounded-xl p-5 bg-cobalt/5">
                  <h3 className="font-semibold text-silver-text text-sm mb-2">Dedicated Project Management</h3>
                  <p className="text-xs text-silver-muted leading-relaxed">Into23 assigned dedicated project managers aligned with the client's time zone, ensuring real-time communication, rapid issue resolution, and seamless coordination across all language teams.</p>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <div className="glow-card rounded-xl p-6">
                <h3 className="font-semibold text-silver-text mb-4">Project Details</h3>
                <dl className="space-y-3 text-sm">
                  <div><dt className="text-silver-muted text-xs uppercase tracking-wider mb-1">Industry</dt><dd className="text-silver-text">E-Commerce</dd></div>
                  <div><dt className="text-silver-muted text-xs uppercase tracking-wider mb-1">Content Type</dt><dd className="text-silver-text">App interface localization</dd></div>
                  <div><dt className="text-silver-muted text-xs uppercase tracking-wider mb-1">Languages</dt><dd className="text-silver-text">20+ languages</dd></div>
                  <div><dt className="text-silver-muted text-xs uppercase tracking-wider mb-1">Daily Throughput</dt><dd className="text-silver-text">15,000+ words/day/language</dd></div>
                  <div><dt className="text-silver-muted text-xs uppercase tracking-wider mb-1">Custom Tooling</dt><dd className="text-silver-text">Into23-built text extraction tool</dd></div>
                </dl>
              </div>

              <div className="glow-card rounded-xl p-6">
                <h3 className="font-semibold text-silver-text mb-3">Key Capabilities Used</h3>
                <ul className="space-y-2 text-sm text-silver-muted">
                  {['Custom text extraction tooling', 'eCommerce-expert linguists', 'CAT tool QA', 'Style guide development', 'Glossary management', 'Timezone-aligned PMs', 'Cultural adaptation']?.map((cap) => (
                    <li key={cap} className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-cobalt-glow flex-shrink-0" />
                      {cap}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="glow-card rounded-xl p-6">
                <h3 className="font-semibold text-silver-text mb-3">Scale Your eCommerce?</h3>
                <p className="text-sm text-silver-muted mb-4">Talk to our team about multilingual eCommerce localization.</p>
                <Link href="/get-a-quote">
                  <span className="block text-center px-4 py-3 bg-cobalt hover:bg-cobalt-glow text-white text-sm font-semibold rounded-lg transition-all">
                    Get a Quote
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
